import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Pressable,
  Platform,
  useWindowDimensions,
  Dimensions,
  BackHandler,
  Keyboard,
  TextInput,
} from 'react-native';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useKeyboardVisible, useKeyboardHeight, useWebKeyboard } from '@/hooks/useKeyboard';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { usePathname } from 'expo-router';
import {
  BottomSheetModal,
  INITIAL_LAYOUT_VALUE,
  useBottomSheetInternal,
  type BottomSheetBackdropProps,
} from '@gorhom/bottom-sheet';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import {
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from '@/components/gluestack/modal';
import {
  Drawer,
  DrawerBackdrop,
  DrawerContent,
  DrawerHeader,
  DrawerCloseButton,
  DrawerBody,
} from '@/components/gluestack/drawer';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { Cancel01Icon, Cancel02Icon } from '@hugeicons-pro/core-solid-rounded';
import { Heading } from '@/components/gluestack/heading';
import { HStack } from '../gluestack/hstack';
import { CountBadge } from '~/components/ui/CountAndMyTypeBadge';
import { BottomSheetPlatformBackdrop } from '@/components/ui/BottomSheetPlatformBackdrop';

// ─── Constants ───────────────────────────────────────────────────────────────

const isWeb = Platform.OS === 'web';

type ModalHeight = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

const MODAL_HEIGHTS: Record<ModalHeight, number> = {
  xs: 220, sm: 280, md: 360, lg: 440, xl: 520, full: 520,
};

const SNAP_POINT_HEIGHTS: Record<ModalHeight, string> = {
  xs: '30%', sm: '40%', md: '50%', lg: '65%', xl: '75%', full: '85%',
};

const HANDLE_HEIGHT = 26; // py-2.5 (20px) + h-1.5 (6px)
export const KEYBOARD_OPEN_SNAP_POINT = '90%';
const KEYBOARD_OPEN_SNAP_PERCENT = parseInt(KEYBOARD_OPEN_SNAP_POINT, 10);

/**
 * While the keyboard is up on `expectsKeyboard` native sheets, squeezes every snap except the
 * highest into a 1%-spaced ladder ending at `max-1`, preserving snap count and strictly increasing
 * values (Gorhom stays stable; removing detents or using duplicates both misbehave).
 */
function snapPointsCompressForKeyboardLock(snapPercentStrings: string[]): string[] {
  const parsed = snapPercentStrings.map((p) => parseInt(String(p), 10));
  const n = parsed.length;
  if (n <= 1) return snapPercentStrings;

  const maxP = parsed[n - 1];
  const lowerCount = n - 1;
  const out = [...snapPercentStrings];

  for (let i = 0; i < lowerCount; i++) {
    const stepsFromMax = lowerCount - i;
    const v = maxP - stepsFromMax;
    out[i] = `${Math.max(1, v)}%`;
  }
  out[n - 1] = `${maxP}%`;
  return out;
}

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Static config attached to a content component via `Component.sheetConfig`.
 * Provides defaults that can be overridden by IContextSheetProps at the call site.
 * Priority: IContextSheetProps (instance) → ISheetConfig (static) → built-in defaults.
 */
export interface ISheetConfig {
  snapPoints?: string[];
  additionalSnapPoints?: string[];
  largeScreenMode?: 'modal' | 'drawer';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'full';
  /** Controls snap point (%) on small screens and modal height (px) on large screens. Defaults to `'md'`. */
  height?: ModalHeight;
  flushBottom?: boolean;
  scrollableContent?: boolean;
  /**
   * When `scrollableContent` is true on the native `@gorhom/bottom-sheet` surface, forwards vertical
   * drag-at-scroll-top to the sheet (dismiss animation) instead of only the handle rail. Requires a
   * Gorhom-linked scroll wrapper (`SheetScrollView`, or FlashList `renderScrollComponent` from
   * `useBottomSheetScrollableCreator`). Plain RN `ScrollView` without that link should omit this flag.
   */
  integratedNativeSheetScrollGestures?: boolean;
  /** Cap snap points on mobile web when the virtual keyboard shrinks the viewport. */
  adjustForWebKeyboard?: boolean;
  /** Hides the close button in the bottom sheet (small screens). The close button is shown by default. */
  hideCloseButton?: boolean;
  /** Opens native bottom sheets at a keyboard-ready snap point when the content auto-focuses an input. */
  expectsKeyboard?: boolean;
}

/**
 * Instance props passed to `<ContextSheet />` at the call site.
 * Inherits all layout props from ISheetConfig; instance values override the
 * content component's static sheetConfig.
 * Priority: IContextSheetProps (instance) → ISheetConfig (static) → built-in defaults.
 */
interface IContextSheetProps extends ISheetConfig {
  isOpen: boolean;
  onClose: () => void;
  contentComponent: React.ComponentType<any> & { sheetConfig?: ISheetConfig };
  payload?: any;
  title?: string;
  shortTitle?: string;
  /** Merges `expectsKeyboard` into the payload so content components (e.g. CommentsSheet) can auto-focus their text input. */
  expectsKeyboard?: boolean;
  /** Fires when the bottom sheet starts its dismiss animation (before `onClose`). */
  onWillClose?: () => void;
}

/** Props forwarded to the rendered content component inside the sheet. */
export interface IContextSheetContentProps {
  onClose?: () => void;
  onExpand?: () => void;
  onCollapse?: () => void;
  payload?: any;
  title?: string;
  shortTitle?: string;
  setTitleCount?: (count: number) => void;
  setScrollableContent?: (scrollable: boolean) => void;
}

/** Extends a scrollable element to the drawer's right edge so the scrollbar sits flush against the border. */
export const DRAWER_SCROLL_CLASSES = {
  className: '-mr-5 md:-mr-6',
  contentContainerClassName: 'mr-5',
} as const;

// ─── Helper Components ───────────────────────────────────────────────────────

function TitleWithCount({ text, size, center = false, count }: {
  text: string;
  size: 'md' | 'lg';
  center?: boolean;
  count: number;
}) {
  return (
    <HStack className="items-baseline">
      <Heading size={size} isTruncated className={center ? 'text-center' : undefined}>{text}</Heading>
      <CountBadge count={count} size="sm" textClassName="text-typography-700" className="ml-2 bg-background-200" abbreviate={false} />
    </HStack>
  );
}

/**
 * Constrains children to the visible area below the handle, resizing smoothly between snap points.
 * Uses Gorhom's measured `containerHeight` and `handleHeight` (not screen height or a fixed handle
 * constant) so `flushBottom` sheets sit flush on native without a few-pixel gap at the bottom.
 */
function BottomSheetContentArea({ children }: { children: React.ReactNode }) {
  const { animatedPosition, animatedLayoutState } = useBottomSheetInternal();
  const { height: windowHeight } = useWindowDimensions();

  // On web, track the max window height seen so virtual keyboards
  // (which shrink the viewport) don't reduce the content area.
  const stableHeightRef = useRef(windowHeight);
  if (isWeb) {
    stableHeightRef.current = Math.max(stableHeightRef.current, windowHeight);
  }

  const animatedStyle = useAnimatedStyle(() => {
    'worklet';
    const { containerHeight, handleHeight } = animatedLayoutState.get();

    const resolvedContainerHeight =
      containerHeight !== INITIAL_LAYOUT_VALUE && containerHeight != null
        ? containerHeight
        : isWeb
          ? stableHeightRef.current
          : Dimensions.get('window').height;

    const resolvedHandleHeight =
      handleHeight !== INITIAL_LAYOUT_VALUE && handleHeight >= 0
        ? handleHeight
        : HANDLE_HEIGHT;

    const visibleHeight =
      resolvedContainerHeight - animatedPosition.value - resolvedHandleHeight;
    return { height: Math.max(visibleHeight, 0) };
  });

  return (
    <Animated.View style={animatedStyle}>
      <View style={{ flex: 1, overflow: 'hidden' }}>
        {children}
      </View>
    </Animated.View>
  );
}

const renderBackground = (props: any) => (
  <View {...props} className="bg-background-50 rounded-t-3xl" />
);

const renderHandle = (props: any) => (
  <View {...props} className="items-center bg-background-50 rounded-t-3xl py-2.5">
    <View className="h-1.5 w-10 rounded-full bg-background-400" />
  </View>
);

const dismissNativeKeyboardAndBlurInput = () => {
  if (isWeb) return;

  const focusedInput = TextInput.State.currentlyFocusedInput();
  if (focusedInput) {
    TextInput.State.blurTextInput(focusedInput);
  }
  Keyboard.dismiss();
};

// ─── Main Component ──────────────────────────────────────────────────────────

export function ContextSheet({
  isOpen,
  onClose,
  contentComponent: ContentComponent,
  payload,
  title,
  shortTitle,
  hideCloseButton: hideCloseButtonProp,
  size: sizeProp,
  height: heightProp,
  snapPoints: snapPointsProp,
  additionalSnapPoints: additionalSnapPointsProp,
  largeScreenMode: largeScreenModeProp,
  flushBottom: flushBottomProp,
  scrollableContent: scrollableContentProp,
  integratedNativeSheetScrollGestures:
    integratedNativeSheetScrollGesturesProp,
  adjustForWebKeyboard: adjustForWebKeyboardProp,
  expectsKeyboard: expectsKeyboardProp,
  onWillClose,
}: IContextSheetProps) {
  // ── Config resolution (instance props → sheetConfig → defaults) ──

  const sheetConfig: ISheetConfig = (ContentComponent as any).sheetConfig ?? {};
  const largeScreenMode = largeScreenModeProp ?? sheetConfig.largeScreenMode ?? 'drawer';
  const size = sizeProp ?? sheetConfig.size;
  const flushBottom = flushBottomProp ?? sheetConfig.flushBottom ?? false;
  const adjustForWebKeyboard =
    adjustForWebKeyboardProp ?? sheetConfig.adjustForWebKeyboard ?? false;
  const expectsKeyboard = expectsKeyboardProp ?? sheetConfig.expectsKeyboard ?? false;
  const modalHeight = heightProp ?? sheetConfig.height ?? 'md';
  const integratedNativeSheetScrollGestures =
    integratedNativeSheetScrollGesturesProp ??
    sheetConfig.integratedNativeSheetScrollGestures ??
    false;

  const explicitSnapPoints = snapPointsProp ?? sheetConfig.snapPoints;
  const additionalSnapPoints = additionalSnapPointsProp ?? sheetConfig.additionalSnapPoints;
  const baseSnapPoints = explicitSnapPoints ?? [SNAP_POINT_HEIGHTS[modalHeight]];
  const configuredSnapPoints = additionalSnapPoints
    ? [...baseSnapPoints, ...additionalSnapPoints]
    : baseSnapPoints;

  // ── Layout & keyboard state ──

  const isLargeScreen = useIsLargeScreen();
  const hideCloseButton =
    !isLargeScreen && (hideCloseButtonProp ?? sheetConfig.hideCloseButton ?? false);
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const expectsNativeKeyboardOnOpen = !isWeb && !isLargeScreen && expectsKeyboard;

  const snapPoints = useMemo(() => {
    if (!expectsNativeKeyboardOnOpen) return configuredSnapPoints;

    const hasKeyboardOpenSnapPoint = configuredSnapPoints.some(
      (point) => parseInt(point, 10) >= KEYBOARD_OPEN_SNAP_PERCENT
    );
    return hasKeyboardOpenSnapPoint
      ? configuredSnapPoints
      : [...configuredSnapPoints, KEYBOARD_OPEN_SNAP_POINT];
  }, [configuredSnapPoints, expectsNativeKeyboardOnOpen]);

  const isKeyboardVisible = useKeyboardVisible();
  const keyboardHeight = useKeyboardHeight();
  const { isWebKeyboardVisible, webKeyboardHeight } = useWebKeyboard();
  const finalKeyboardHeight = Platform.OS === 'android' ? keyboardHeight + 24 : keyboardHeight;

  // ── Shared state ──

  const [titleCount, setTitleCount] = useState<number>(0);
  const [scrollableContent, setScrollableContent] = useState(scrollableContentProp ?? sheetConfig.scrollableContent ?? false);

  // On web, suppress FocusScope's autoFocus to prevent the virtual keyboard
  // from appearing before the overlay animation settles on mobile/tablet web,
  // and to avoid scroll-into-view during slide animations on desktop web.
  const noAutoFocusRef = useRef(null);
  const autoFocusGuardRef = isWeb ? noAutoFocusRef : undefined;

  // ── Bottom sheet refs & derived snap points ──

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  // Track the max window height seen so keyboard-induced viewport shrinkage
  // (which reduces windowHeight on some mobile browsers) doesn't double-count.
  const stableWindowHeightRef = useRef(windowHeight);
  if (isWeb && windowHeight > stableWindowHeightRef.current) {
    stableWindowHeightRef.current = windowHeight;
  }

  const gorhomSnapPoints = useMemo(() => {
    if (
      !isWeb &&
      expectsNativeKeyboardOnOpen &&
      isKeyboardVisible &&
      snapPoints.length > 1
    ) {
      return snapPointsCompressForKeyboardLock(snapPoints);
    }

    // Opening at max snap with autofocus: Gorhom's keyboard behavior already adjusts the sheet.
    // Inflating snap percents here when the keyboard appears causes a second layout pass and feels like the keyboard animates twice.
    if (
      !isWeb &&
      isKeyboardVisible &&
      finalKeyboardHeight > 0 &&
      !expectsNativeKeyboardOnOpen
    ) {
      const screenHeight = Dimensions.get('screen').height;
      const netKeyboardHeight = Math.max(finalKeyboardHeight - insets.bottom, 0);
      const keyboardPercent = Math.round((netKeyboardHeight / screenHeight) * 100);
      return snapPoints.map((p) => {
        const n = parseInt(p, 10);
        return `${Math.min(n + keyboardPercent, KEYBOARD_OPEN_SNAP_PERCENT)}%`;
      });
    }
    if (isWeb && adjustForWebKeyboard && webKeyboardHeight > 0) {
      const fullHeight = stableWindowHeightRef.current;
      const availableHeight = Math.min(windowHeight, fullHeight - webKeyboardHeight) - insets.top;
      const maxPercent = Math.floor((availableHeight / fullHeight) * 100);
      return snapPoints.map((p) => {
        const n = parseInt(p, 10);
        return `${Math.min(n, maxPercent)}%`;
      });
    }
    return snapPoints;
  }, [
    snapPoints,
    expectsNativeKeyboardOnOpen,
    isKeyboardVisible,
    finalKeyboardHeight,
    insets.bottom,
    adjustForWebKeyboard,
    webKeyboardHeight,
    windowHeight,
    insets.top,
  ]);

  const initialBottomSheetIndex = expectsNativeKeyboardOnOpen ? gorhomSnapPoints.length - 1 : 0;

  // ── Effects ──

  const pathname = usePathname();
  const lastPathnameRef = useRef<string | null>(null);

  const handleClose = useCallback(() => {
    dismissNativeKeyboardAndBlurInput();
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      dismissNativeKeyboardAndBlurInput();
    }
  }, [isOpen]);

  useEffect(() => {
    if (lastPathnameRef.current === null) {
      lastPathnameRef.current = pathname ?? null;
      return;
    }
    if (pathname !== lastPathnameRef.current) {
      lastPathnameRef.current = pathname ?? null;
      if (isOpen) {
        setTimeout(() => {
          handleClose();
        }, 50);
      }
    }
  }, [pathname, isOpen, handleClose]);

  useEffect(() => {
    if (!isWeb || !isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener('keyup', handler, true);
    return () => document.removeEventListener('keyup', handler, true);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isLargeScreen && isOpen) {
      bottomSheetRef.current?.present();
    }
  }, [isLargeScreen, isOpen]);

  // Android hardware back button: close the sheet/modal/drawer instead of
  // letting the press fall through to the navigator (which would pop the
  // current screen out from under the open sheet).
  useEffect(() => {
    if (Platform.OS !== 'android' || !isOpen) return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      handleClose();
      return true;
    });
    return () => sub.remove();
  }, [isOpen, handleClose]);

  // ── Callbacks ──

  const handleAnimate = useCallback(
    (_fromIndex: number, toIndex: number) => {
      if (toIndex === -1) {
        dismissNativeKeyboardAndBlurInput();
        onWillClose?.();
      }
    },
    [onWillClose]
  );

  const handleExpand = useCallback(() => {
    if (!isLargeScreen && gorhomSnapPoints.length > 1) {
      bottomSheetRef.current?.snapToIndex(gorhomSnapPoints.length - 1);
    }
  }, [isLargeScreen, gorhomSnapPoints.length]);

  const handleCollapse = useCallback(() => {
    if (!isLargeScreen) {
      bottomSheetRef.current?.snapToIndex(0);
    }
  }, [isLargeScreen]);

  const renderBackdrop = useCallback((props: BottomSheetBackdropProps) => {
    return (
      <BottomSheetPlatformBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.5} />
    );
  }, []);

  // ── Content props (shared across all rendering modes) ──

  const contentProps: IContextSheetContentProps = {
    onClose: handleClose,
    onExpand: handleExpand,
    onCollapse: handleCollapse,
    title,
    shortTitle,
    payload: expectsKeyboard ? { ...payload, expectsKeyboard } : payload,
    setTitleCount,
    setScrollableContent,
  };

  // ── Early return ──

  if (!isOpen || !ContentComponent) {
    return null;
  }

  // ── Large screen: Drawer or Modal ──

  if (isLargeScreen) {
    const drawerSize = size === 'xs' ? 'sm' : size;

    if (largeScreenMode === 'drawer') {
      return (
        <Drawer
          isOpen={isOpen}
          onClose={handleClose}
          size={drawerSize}
          anchor="right"
          initialFocusRef={autoFocusGuardRef}>
          <DrawerBackdrop />
          <DrawerContent style={flushBottom ? { paddingBottom: 0 } : undefined}>
            <KeyboardAvoidingView behavior="padding" style={{ flex: 1 }}>
              {(title || !hideCloseButton) && (
                <DrawerHeader>
                  {title ? (
                    <TitleWithCount text={title} size="md" count={titleCount} />
                  ) : <View className="flex-1" />}
                  {!hideCloseButton && (
                    <DrawerCloseButton tabIndex={-1}>
                      <HugeIconsWrapper icon={Cancel01Icon} className="ml-6 text-neutral-500" />
                    </DrawerCloseButton>
                  )}
                </DrawerHeader>
              )}
              <DrawerBody className={flushBottom ? undefined : 'mb-6'}>
                <ContentComponent {...contentProps} />
              </DrawerBody>
            </KeyboardAvoidingView>
          </DrawerContent>
        </Drawer>
      );
    }

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size={size}
        initialFocusRef={autoFocusGuardRef}>
        <ModalBackdrop />
        <ModalContent
          style={[
            { height: MODAL_HEIGHTS[modalHeight] },
            !isWeb && isKeyboardVisible && finalKeyboardHeight > 0
              ? { maxHeight: windowHeight - finalKeyboardHeight - insets.top - 24 }
              : undefined,
          ]}
        >
          {(title || !hideCloseButton) && (
            <ModalHeader>
              {title ? (
                <TitleWithCount text={title} size="lg" count={titleCount} />
              ) : <View className="flex-1" />}
              {!hideCloseButton && (
                <ModalCloseButton tabIndex={-1}>
                  <HugeIconsWrapper icon={Cancel01Icon} className="text-neutral-500" />
                </ModalCloseButton>
              )}
            </ModalHeader>
          )}
          <ModalBody>
            <ContentComponent {...contentProps} />
          </ModalBody>
        </ModalContent>
        {!isWeb && isKeyboardVisible && finalKeyboardHeight > 0 && (
          <View style={{ height: finalKeyboardHeight, pointerEvents: 'none' }} />
        )}
      </Modal>
    );
  }

  // ── Small screen: Bottom Sheet ──

  return (
    <BottomSheetModal
      ref={bottomSheetRef}
      index={initialBottomSheetIndex}
      snapPoints={gorhomSnapPoints}
      onDismiss={handleClose}
      onAnimate={handleAnimate}
      backdropComponent={renderBackdrop}
      backgroundComponent={renderBackground}
      handleComponent={renderHandle}
      stackBehavior="push"
      enableDynamicSizing={false}
      enablePanDownToClose
      enableContentPanningGesture={
        scrollableContent ? integratedNativeSheetScrollGestures : true
      }
      enableBlurKeyboardOnGesture={!isWeb}
    >
      <BottomSheetContentArea>
        <View style={{ flex: 1, paddingHorizontal: 20 }}>
          {(shortTitle || title || !hideCloseButton) && (
            <HStack className="mb-4 w-full items-start">
              {hideCloseButton ? (
                <View className="flex-1 items-center">
                  {(shortTitle || title) && (
                    <TitleWithCount text={(shortTitle ?? title)!} size="md" center count={titleCount} />
                  )}
                </View>
              ) : (
                <>
                  <View className="mr-3 flex-1">
                    {shortTitle || title ? (
                      <TitleWithCount text={(shortTitle ?? title)!} size="md" count={titleCount} />
                    ) : (
                      <View />
                    )}
                  </View>
                  <Pressable
                    className="shrink-0 h-7 w-7 items-center justify-center rounded-full bg-background-200"
                    onPress={handleClose}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    tabIndex={-1}
                  >
                    <HugeIconsWrapper icon={Cancel02Icon} size={16} className="text-typography-500" />
                  </Pressable>
                </>
              )}
            </HStack>
          )}

          <View style={{ flex: 1 }}>
            <ContentComponent {...contentProps} />
          </View>

          {!flushBottom && (
            <View
              style={{
                height: !isWeb && isKeyboardVisible
                  ? finalKeyboardHeight + 16
                  : isWebKeyboardVisible ? 16 : insets.bottom + 16,
              }}
            />
          )}
        </View>
      </BottomSheetContentArea>
    </BottomSheetModal>
  );
}
